import { useEffect, useRef, useState } from 'react';
import cc from 'classcat';
import { useContainer } from '../../../hooks';
import { GlobalApi } from '../../../util/Global';
import { FormikProps } from 'formik';
import { IMenuItem } from '../../Common';

interface DropdownProps<T> {
    label?: string;
    id?: string;
    menuItems: IMenuItem[];
    isOpen?: boolean;
    selected: IMenuItem;
    className?: string;
    onChange: (item: IMenuItem) => void;
    onToggle?: (open: boolean) => void;
    context?: FormikProps<T>;
    iconUrl?: string;
    testId?: string;
    isOpenAbove?: boolean;
    isError?: boolean;
    hasInfo?: boolean;
    tooltipInfoText?: string;
    tooltipStyle?: string;
    disabled?: boolean;
    customButtonFieldLabel?: string;
    customButtonFieldOnClick?: () => void;
}

export const Dropdown = <T extends object>({
    label,
    id,
    menuItems,
    selected,
    className,
    iconUrl,
    onChange,
    onToggle,
    testId,
    isOpenAbove = false,
    isOpen,
    isError,
    hasInfo,
    tooltipInfoText,
    tooltipStyle,
    disabled,
    context,
    customButtonFieldLabel,
    customButtonFieldOnClick,
}: DropdownProps<T>) => {
    const [openMenu, setOpenMenu] = useState<boolean>(isOpen || false);
    const [openAbove, _] = useState<boolean>(isOpenAbove);
    const allItems = useRef<HTMLButtonElement[]>([]);
    const globalApi: GlobalApi = useContainer(GlobalApi);

    const error = context?.errors?.[id as keyof T];
    const touched = context?.touched?.[id as keyof T];
    const showError = Boolean(error && touched);

    const addItemToRef = (item: HTMLButtonElement | null) => {
        if (item) allItems.current.push(item);
    };

    useEffect(() => {
        if (!openMenu || allItems.current.length === 0) return;

        if (allItems.current.length > 10) {
            allItems.current.forEach(item => {
                if (item && +item.id === selected.value) {
                    item.scrollIntoView();
                }
            });
        } else {
            const lastItem = allItems.current[allItems.current.length - 1];
            if (lastItem && lastItem.getBoundingClientRect().y + 30 > globalApi.innerHeight()) {
                lastItem.scrollIntoView();
            }
        }
    }, [openMenu]);

    useEffect(() => {
        if (typeof isOpen === 'boolean') {
            setOpenMenu(isOpen);
        }
    }, [isOpen]);

    const changeSelect = (item: IMenuItem) => {
        onChange(item);
        setOpenMenu(false);
    };

    const checkOnBlur = (e: React.FocusEvent<HTMLButtonElement, Element>) => {
        if (!e.relatedTarget?.classList.contains('dropdown__menu--item')) setOpenMenu(false);
    };

    const handleKeyDownChangeItem = (next: number) => {
        const newItem = menuItems[next];

        allItems.current.forEach(item => {
            if (+item.id === newItem.value) {
                item.scrollIntoView();
            }
        });

        onChange(newItem);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const currentIndex = menuItems.findIndex(item => item.value === selected.value);

        if (e.key === 'ArrowUp') {
            if (currentIndex === 0) {
                handleKeyDownChangeItem(menuItems.length - 1);
            }
            if (currentIndex > 0) {
                handleKeyDownChangeItem(currentIndex - 1);
            }
        }

        if (e.key === 'ArrowDown') {
            if (currentIndex === menuItems.length - 1) {
                handleKeyDownChangeItem(0);
            }
            if (currentIndex < menuItems.length - 1) {
                handleKeyDownChangeItem(currentIndex + 1);
            }
        }

        if (e.key === 'Enter') {
            changeSelect(menuItems[currentIndex]);
        }
    };

    const toggleMenu = () => {
        const newOpenState = !openMenu;
        setOpenMenu(newOpenState);
        if (onToggle) onToggle(newOpenState);
    };

    return (
        <div className={cc(['dropdown paragraph-4', className])}>
            {label && (
                <label htmlFor={id} className="dropdown__label paragraph-4">
                    {label}
                    {hasInfo && (
                        <div className="dropdown__label-info-wrapper">
                            <img className="dropdown__label-info-wrapper--icon" src="/svg/slider/info-icon.svg" alt="info" />
                            <p className={cc([`paragraph-5 dropdown__label-info-wrapper--tooltip ${tooltipStyle}`])}>{tooltipInfoText}</p>
                        </div>
                    )}
                </label>
            )}
            {iconUrl ? (
                <button
                    type="button"
                    className="dropdown__icon"
                    onClick={toggleMenu}
                    onBlur={checkOnBlur}
                    onKeyDown={handleKeyDown}
                    data-testid={testId}
                >
                    <img src={iconUrl} alt="icon" />
                </button>
            ) : (
                <button
                    type="button"
                    className={`${isError && 'input-style__field--error'} dropdown__select`}
                    onClick={toggleMenu}
                    onBlur={checkOnBlur}
                    onKeyDown={handleKeyDown}
                    data-testid={testId}
                    disabled={disabled || false}
                >
                    <div className={cc(['dropdown__selected-item', openMenu && 'dropdown__selected-item--active'])}>
                        <p className="paragraph-4">{selected?.label || 'Select'}</p>
                        {openMenu ? <img src="/svg/arrow-up.svg" alt="Arrow Down" /> : <img src="/svg/arrow-down.svg" alt="Arrow Up" />}
                    </div>
                </button>
            )}
            {openMenu && (
                <div className={cc(['dropdown__menu', { 'dropdown__menu--icon': iconUrl, 'dropdown__menu--above': openAbove }])}>
                    {menuItems?.length === 0 ? (
                        <div className="dropdown__menu--empty">No options available</div>
                    ) : (
                        menuItems?.map(option => (
                            <button
                                type="button"
                                id={option.value.toString()}
                                ref={el => addItemToRef(el)}
                                className={cc([
                                    'dropdown__menu--item',
                                    'paragraph-4',
                                    selected.value === option.value && 'dropdown__menu--item-active',
                                ])}
                                key={option.value}
                                onClick={() => changeSelect(option)}
                                data-testid="menu-item"
                            >
                                {option.label}
                            </button>
                        ))
                    )}
                    {customButtonFieldLabel && (
                        <button
                            type="button"
                            onClick={customButtonFieldOnClick}
                            data-testid="custom-button"
                            className={cc(['dropdown__menu--item', 'paragraph-4', 'dropdown__menu--custom-button'])}
                        >
                            {customButtonFieldLabel}
                            <img src="/svg/setting-blue-outlined.svg" alt="setting" />
                        </button>
                    )}
                </div>
            )}
            {showError && <span className={'input-style__error-msg'}>{error as string}</span>}
        </div>
    );
};
